import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {exhaustMap, tap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, Observable} from 'rxjs';
import {HttpErrorResponse} from "@angular/common/http";
import firebase from "firebase/compat/app";
import {onValue, ref} from "firebase/database";
import Unsubscribe = firebase.Unsubscribe;
import {
  RecommendationHolderType,
  RecommendationModel
} from "../../models/recommendation.model";
import {RecommendationHttpService} from "../services/recommendation-http.service";
import * as _ from 'lodash';
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";

export interface RecommendationsComponentState {
  recommendations: RecommendationModel[];
  cursor?: string;
  loading: boolean;
  error: string | null;
}

@Injectable()
export class RecommendationsComponentStore extends ComponentStore<RecommendationsComponentState> {

  // Selectors
  readonly recommendations$ = this.select((state) => state.recommendations);
  readonly loading$ = this.select((state) => state.loading);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);

  vm$ = this.select({
    recommendations: this.recommendations$,
    loading: this.loading$,
    error: this.error$,
    cursor: this.cursor$
  })

  private channelsUnsubscribeMap: Map<string, Unsubscribe> = new Map();

  constructor(
    @Inject(RecommendationHttpService) private recommendationHttpService: RecommendationHttpService,
    @Inject(CommonsFacadeService) private commonsFacadeService: CommonsFacadeService,
    @Inject('firebaseService') private firebaseService
  ) {
    super({
      recommendations: [],
      cursor: '',
      loading: false,
      error: null,
    });
  }

  // Updaters
  readonly onSetRecommendations = this.updater((state: RecommendationsComponentState, value: {
    recommendations: RecommendationModel[],
    cursor?: string
  }) => ({
    ...state,
    recommendations: _.uniqBy([...state.recommendations, ...value.recommendations], 'id'),
    cursor: value.cursor,
    loading: false,
    error: null,
  }));

  readonly onSetCreatedRecommendation = this.updater((state: RecommendationsComponentState, recommendation: RecommendationModel[]) => ({
    ...state,
    recommendations: _.uniqBy([...recommendation, ...state.recommendations], 'id'),
    loading: false,
    error: null,
  }));

  readonly onUpdateRecommendation = this.updater((state: RecommendationsComponentState, updatedRecommendation: RecommendationModel) => ({
    ...state,
    recommendations: state.recommendations.map((recommendation: RecommendationModel) => {
        return updatedRecommendation.id === recommendation.id ? {...recommendation, ...updatedRecommendation} : recommendation;
      }
    ),
  }));

  readonly onMarkRecommendationAsRead = this.updater((state: RecommendationsComponentState, markedRecommendation: RecommendationModel) => ({
    ...state,
    recommendations: state.recommendations.filter((recommendation: RecommendationModel) => markedRecommendation.id !== recommendation.id)
  }));

  readonly onResetRecommendations = this.updater((state: RecommendationsComponentState) => ({
    ...state,
    recommendations: [],
    cursor: null,
    loading: false,
    error: null,
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  // Effect for fetching recommendations
  readonly getRecommendations = this.effect(
    (trigger$: Observable<any>) =>
      trigger$.pipe(
        tap(() => this.setLoading(true)), // Set loading state
        withLatestFrom(this.cursor$, this.recommendations$), // Combine with cursor from the state
        exhaustMap(([params, cursor, recommendations]) => {
            if (!cursor && recommendations.length > 0 && !params.ids?.length) {
              this.setLoading(false);
              return EMPTY;
            }

            if (params.ids?.length) {

              const recommendationIds = recommendations.map(a => a.id);
              // Find missing IDs (those NOT in the store)
              const idsToRequest = params.ids.filter(id => !recommendationIds.includes(id));

              if (idsToRequest.length === 0) {
                // All requested IDs exist in the store, SKIP API call
                this.setLoading(false);
                return EMPTY;
              }

              // Update params to fetch only missing IDs
              params = {...params, ids: idsToRequest};
            }
            return this.recommendationHttpService.getRecommendations({...params, cursor: cursor || ''}).pipe(
              tap({
                next: (response) => {

                  //Subscribe to recommendations
                  // response.list?.forEach((recommendation: RecommendationModel) => {
                  //   this.subscribe(recommendation.id);
                  // })
                  return this.onSetRecommendations({recommendations: response.list, cursor: response.cursor});
                },
                error: (err) => this.setError(err.message || 'Failed to fetch recommendations'),
                finalize: () => this.setLoading(false)
              })
            )
          }
        )
      )
  );

  // Effect for updating a recommendation
  readonly updateRecommendation = this.effect((recommendation$: Observable<RecommendationModel>) =>
    recommendation$.pipe(
      tap(() => this.commonsFacadeService.setLoaderOn()),
      exhaustMap((recommendation) =>
        this.recommendationHttpService.updateRecommendation(recommendation).pipe(
          tap({
            next: (updatedRecommendation) => {
              this.onUpdateRecommendation(updatedRecommendation);
            },
            error: (err) => this.setError(err.message || 'Failed to update recommendation'),
            finalize: () => this.commonsFacadeService.setLoaderOff()
          })
        )
      )
    )
  );

  readonly markRecommendationAsRead = this.effect((recommendation$: Observable<RecommendationModel>) =>
    recommendation$.pipe(
      tap(() => this.commonsFacadeService.setLoaderOn()),
      exhaustMap((recommendation) =>
        this.recommendationHttpService.markRecommendationAsRead(recommendation).pipe(
          tap({
            next: (markedRecommendation) => {
              this.onMarkRecommendationAsRead(markedRecommendation);
            },
            error: (err) => this.setError(err.message || 'Failed to mark recommendation as read'),
            finalize: () => this.commonsFacadeService.setLoaderOff()
          })
        )
      )
    )
  );



  readonly removeRecommendations = this.updater(
    (state, ids: string[]) => ({
      ...state,
      recommendations: state.recommendations.filter((recommendation) => !ids.includes(recommendation.id))
    })
  );


  // Reset
  readonly resetRecommendations = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.unSubscribeAll();
        this.onResetRecommendations();
      })
    )
  );

  private unSubscribeAll(): void {
    for (const [id, channelUnsubscribe] of this.channelsUnsubscribeMap) {
      channelUnsubscribe();
    }
    this.channelsUnsubscribeMap.clear();
  }

  // Convert Firebase `onValue` into an observable
  public subscribe(id: string) {
    const channel = this.firebaseService.channel;
    const database = this.firebaseService.database;

    const path = `${channel.channelId}/${'recommendations_changed'}/${id}`;
    const dbRef = ref(database, path);

    // Listen for real-time updates
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const recommendation: RecommendationModel = snapshot.val();
        if (!!recommendation) {
          this.onUpdateRecommendation(recommendation);
        }
      }
    );

    // Cleanup when subscription is closed
    this.channelsUnsubscribeMap.set(id, unsubscribe);
  }

  public subscribeToCreate(holderId: string, insightEntityId?: string) {
    const channel = this.firebaseService.channel;
    const database = this.firebaseService.database;

    const path = `${channel.channelId}/recommendation_created`;
    const dbRef = ref(database, path);

    // Listen for real-time updates
    return onValue(
        dbRef,
        (snapshot) => {
          const recommendations: RecommendationModel[] = snapshot.val();
          if (recommendations?.length) {
            const filteredRecommendations = recommendations.filter(
              recommendation =>
                {
                  const isRelevant: boolean = insightEntityId ? this._isRelevant(recommendation, insightEntityId) : true;

                  return (recommendation.recommendationHolderType === RecommendationHolderType.VisitPlan || recommendation.recommendationHolderType === RecommendationHolderType.Planning) && recommendation.recommendationHolderId === holderId && isRelevant;
                }
            );
            if (filteredRecommendations.length > 0) {
              this.onSetCreatedRecommendation(filteredRecommendations);
            }
          }
        }
    );

    // Cleanup when subscription is closed
    // this.channelsUnsubscribeMap.set(id, unsubscribe);
  }


  private _isRelevant(recommendation: RecommendationModel, id: string): boolean {
    const entities = recommendation?.insightOutput?.insightEntities;

    if (!Array.isArray(entities)) {
      return false;
    }

    return entities.some(entity => entity.entityId === id);
  }
}

